import LoadingComponent from '@/components/common/LoadingComponent';
import { UserMenuDialogMenuProps } from '@/types/props/components/layout';
import { Transition } from '@headlessui/react';
import DefaultButton from '@/components/common/default/DefaultButton';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import handleLanguageChange from '@/utilites/handle-language-change';
import { Locales } from '@/types/enums';
import { useTranslation } from 'next-i18next';
import { Routes } from '@/types/routes.enum';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { HeaderEnum } from '@/types/translations/HeaderEnum';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { FaUser, FaUserPlus } from 'react-icons/fa';
import Svguser from '@/components/svg/header/Svguser';
import Svgdashboard from '@/components/svg/header/Svgdashboard';
import Svgnotifications from '@/components/svg/header/Svgnotifications';
import Svgbuying from '@/components/svg/header/Svgbuying';
import Svgselling from '@/components/svg/header/Svgselling';
import Svgportfolio from '@/components/svg/header/Svgportfolio';
import Svgwishlist from '@/components/svg/header/Svgwishlist';
import Svglastviewed from '@/components/svg/header/Svglastviewed';
import Svgsuggestabottle from '@/components/svg/header/Svgsuggestabottle';
import Svgsettings from '@/components/svg/header/Svgsettings';
import Svglanguage from '@/components/svg/header/Svglanguage';
import Svglogout from '@/components/svg/header/Svglogout';
import Svgchevronleft from '@/components/svg/header/Svgchevronleft';
import Svgchevronright from '@/components/svg/header/Svgchevronright';
import Image from 'next/image';
import languageConstants from '@/constants/language.constants';
import uiItems from '@/constants/ui-items/index.constants';
import Link from 'next/link';
import referralsIcon from '@/public/images/icons/dashboard/referrals.svg';

export default function UserMenuDialogMenu({
  username,
  isLoadingUser,
  notificationsDataLength,
  buyerNotificationsCallToAc,
  sellerNotificationsCallToAc,
  user,
}: UserMenuDialogMenuProps) {
  const { translate } = useTypedTranslation<HeaderEnum>('header');
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  const [doingLanguageChange, setDoingLanguageChange] = useState(false);
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const router = useRouter();
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const foundCountry =
      languageConstants.localesWithIcons[
        (router.locale ?? 'en') as keyof typeof languageConstants.localesWithIcons
      ];
    return {
      label: router.locale ?? 'en',
      country: foundCountry,
    };
  });

  const [currentSubMenuActive, setCurrentSubMenuActive] = useState<
    'none' | 'language' | 'settings'
  >('none');

  async function handleChangeLanguage(locale: Locales) {
    setDoingLanguageChange(true);
    await handleLanguageChange(locale, tCommon, router, Boolean(user), queryClient);
    setDoingLanguageChange(false);
  }

  useEffect(() => {
    const foundCountry =
      languageConstants.localesWithIcons[
        (router.locale ?? 'en') as keyof typeof languageConstants.localesWithIcons
      ];
    setSelectedLanguage({
      label: router.locale ?? 'en',
      country: foundCountry,
    });
  }, [router.locale]);

  return (
    <>
      {/* MAIN MENU */}
      <Transition
        show={currentSubMenuActive === 'none'}
        enter="tw-transition-transform tw-duration-150"
        enterFrom="tw-transform tw--translate-x-full"
        enterTo="tw-transform tw-translate-x-0"
        leave="tw-transition-transform tw-duration-150"
        leaveFrom="tw-transform tw-translate-x-0"
        leaveTo="tw-transform tw--translate-x-full"
        as={'div'}
      >
        <div>
          <LoadingComponent
            as="div"
            componentProps={{
              className:
                'tw-py-6 tw-border tw-border-solid tw-border-platinum-gray tw-border-t-0 tw-border-l-0 tw-border-r-0',
            }}
            loading={isLoadingUser}
          >
            {username ? (
              <div className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline">
                <Svguser width={23} height={23} />
                <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                  {username}
                </p>
              </div>
            ) : (
              <ul className="tw-mb-0 tw-flex tw-list-none tw-flex-col tw-gap-4 tw-p-0">
                <div className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline">
                  <FaUser className="tw-text-xl tw-text-[#54606F]" />
                  {username ? (
                    <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                      {username}
                    </p>
                  ) : (
                    <Link
                      className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue tw-no-underline"
                      href="/auth/sign-in"
                    >
                      {translateCommon(CommonEnum['sign_in'])}
                    </Link>
                  )}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline">
                  <FaUserPlus className="tw-text-2xl tw-text-[#54606F]" />
                  {username ? (
                    <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                      {username}
                    </p>
                  ) : (
                    <Link
                      className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue tw-no-underline"
                      href="/auth/sign-up"
                    >
                      {translateCommon(CommonEnum['sign_up'])}
                    </Link>
                  )}
                </div>
              </ul>
            )}
          </LoadingComponent>
          <div>
            <ul className="tw-flex tw-list-none tw-flex-col tw-gap-4 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-platinum-gray tw-p-0 tw-pb-4 tw-pt-5">
              <li className="tw-pb-2">
                <a
                  className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline"
                  href={Routes._DASHBOARD}
                >
                  <Svgdashboard width={24} height={24} fill="#54606F" />
                  <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                    {translate(HeaderEnum['dashboard_menu.main.dashboard'])}
                  </p>
                </a>
              </li>
              <li className="tw-pb-2">
                <a
                  className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-no-underline"
                  href={Routes._DASHBOARD_NOTIFICATIONS}
                >
                  <div className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue">
                    <Svgnotifications width={24} height={24} />
                    <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                      {translate(HeaderEnum['dashboard_menu.main.notifications'])}
                    </p>
                  </div>
                  {notificationsDataLength > 0 && (
                    <span className="tw-rounded-full tw-bg-main tw-px-2 tw-py-1 tw-text-sm-higher tw-font-semibold tw-leading-[17.6px] tw-text-white">
                      {notificationsDataLength}
                    </span>
                  )}
                </a>
              </li>
              <li className="tw-pb-2">
                <a
                  className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-no-underline"
                  href={Routes._DASHBOARD_BUYING}
                >
                  <div className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline">
                    <Svgbuying width={24} height={24} />
                    <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                      {translate(HeaderEnum['dashboard_menu.main.buying'])}
                    </p>
                  </div>
                  {buyerNotificationsCallToAc > 0 && (
                    <span className="tw-rounded-full tw-bg-main tw-px-2 tw-py-1 tw-text-sm-higher tw-font-semibold tw-leading-[17.6px] tw-text-white">
                      {buyerNotificationsCallToAc}
                    </span>
                  )}
                </a>
              </li>
              <li className="tw-pb-2">
                <a
                  className="tw-flex tw-items-center tw-justify-between tw-gap-2 !tw-no-underline"
                  href={Routes._DASHBOARD_SELLING}
                >
                  <div className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline">
                    <Svgselling width={24} height={24} />
                    <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                      {translate(HeaderEnum['dashboard_menu.main.selling'])}
                    </p>
                  </div>
                  {sellerNotificationsCallToAc > 0 && (
                    <span className="tw-rounded-full tw-bg-main tw-px-2 tw-py-1 tw-text-sm-higher tw-font-semibold tw-leading-[17.6px] tw-text-white">
                      {sellerNotificationsCallToAc}
                    </span>
                  )}
                </a>
              </li>
              <li className="tw-pb-2">
                <a
                  className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline"
                  href={Routes._DASHBOARD_PORTFOLIO}
                >
                  <Svgportfolio width={24} height={24} />
                  <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                    {translate(HeaderEnum['dashboard_menu.main.portfolio'])}
                  </p>
                </a>
              </li>
              <li className="tw-pb-2">
                <a
                  className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline"
                  href={Routes._DASHBOARD_WISHLIST}
                >
                  <Svgwishlist width={24} height={24} />
                  <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                    {translate(HeaderEnum['dashboard_menu.main.wishlist'])}
                  </p>
                </a>
              </li>
              <li className="tw-pb-2">
                <a
                  className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline"
                  href={Routes._DASHBOARD_REFERRALS}
                >
                  <Image src={referralsIcon} width={24} height={24} alt="Referrals" />
                  <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                    {translate(HeaderEnum['dashboard_menu.main.referrals'])}
                  </p>
                </a>
              </li>
              <li className="tw-pb-2">
                <a
                  className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline"
                  href={Routes._DASHBOARD_LAST_VIEWED}
                >
                  <Svglastviewed width={24} height={24} />
                  <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                    {translate(HeaderEnum['dashboard_menu.main.last_viewed'])}
                  </p>
                </a>
              </li>
            </ul>
            <ul className="tw-flex tw-list-none tw-flex-col tw-gap-4 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-platinum-gray tw-p-0 tw-py-5">
              <li className="tw-pb-2">
                <a
                  className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline"
                  href={Routes._DASHBOARD_SUGGEST_BOTTLE}
                >
                  <Svgsuggestabottle width={24} height={24} />
                  <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                    {translate(HeaderEnum['dashboard_menu.main.suggest_a_bottle'])}
                  </p>
                </a>
              </li>
            </ul>
            <ul className="tw-flex tw-list-none tw-flex-col tw-gap-4 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-platinum-gray tw-p-0 tw-py-5">
              <li className="tw-w-full tw-pb-2">
                <DefaultButton
                  className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue"
                  onClick={() => setCurrentSubMenuActive('settings')}
                >
                  <div className="tw-flex tw-w-full tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline">
                    <Svgsettings width={24} height={24} />
                    <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                      {translate(HeaderEnum['dashboard_menu.main.settings'])}
                    </p>
                  </div>
                  <Svgchevronright width={6} height={12} className="!tw-text-charcoal-blue" />
                </DefaultButton>
              </li>
              <li className="tw-pb-2">
                <DefaultButton
                  onClick={() => setCurrentSubMenuActive('language')}
                  className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue"
                >
                  <div className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline">
                    <Svglanguage width={24} height={24} />
                    <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                      {translate(HeaderEnum['dashboard_menu.main.language'])}
                    </p>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Image
                        src={selectedLanguage.country.icon}
                        alt={selectedLanguage.label}
                        width={24}
                        height={24}
                      />
                      <strong className="tw-text-base tw-font-semibold tw-uppercase tw-text-slate-gray">
                        {selectedLanguage.label}
                      </strong>
                    </div>
                    <Svgchevronright width={6} height={12} />
                  </div>
                </DefaultButton>
              </li>
            </ul>
            {user && (
              <ul className="tw-flex tw-list-none tw-flex-col tw-gap-4 tw-p-0 tw-py-5">
                <li className="tw-pb-2">
                  <a
                    className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline"
                    href={Routes._AUTH_LOGOUT}
                  >
                    <Svglogout width={24} height={24} />
                    <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                      {translate(HeaderEnum['mobile_menu.sign_out'])}
                    </p>
                  </a>
                </li>
              </ul>
            )}
          </div>
        </div>
      </Transition>
      {/* LANGUAGE MENU */}
      <Transition
        show={currentSubMenuActive === 'language'}
        enter="tw-transition-transform tw-duration-150"
        enterFrom="tw-transform tw--translate-x-full"
        enterTo="tw-transform tw-translate-x-0"
        leave="tw-transition-transform tw-duration-150"
        leaveFrom="tw-transform tw-translate-x-0"
        leaveTo="tw-transform tw--translate-x-full"
        as={'div'}
      >
        <ul className="tw-flex tw-list-none tw-flex-col tw-gap-4 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-platinum-gray tw-p-0 tw-pb-4 tw-pt-5">
          <li>
            <DefaultButton
              className="tw-flex tw-w-full tw-items-center tw-gap-4 tw-pb-2 tw-pl-1 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[20.16px] tw-text-textBlack tw-shadow-sm max-nine:!tw-text-sm nine:tw-min-w-[unset]"
              onClick={() => setCurrentSubMenuActive('none')}
            >
              <Svgchevronleft width={6} height={12} />
              {translate(HeaderEnum['dashboard_menu.main.language'])}
            </DefaultButton>
          </li>
          <li>
            <DefaultButton
              className="tw-flex tw-w-full tw-items-center tw-justify-between tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[20.16px] tw-text-textBlack disabled:tw-animate-pulse disabled:tw-text-gray-600 max-nine:!tw-text-sm nine:tw-min-w-[unset]"
              onClick={() => handleChangeLanguage(Locales.EN)}
              disabled={doingLanguageChange}
            >
              {translate(HeaderEnum['locales.english'])} (English)
            </DefaultButton>
          </li>
          <li>
            <DefaultButton
              className="tw-flex tw-w-full tw-items-center tw-justify-between tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[20.16px] tw-text-textBlack disabled:tw-animate-pulse disabled:tw-text-gray-600 max-nine:!tw-text-sm nine:tw-min-w-[unset]"
              onClick={() => handleChangeLanguage(Locales.ES)}
              disabled={doingLanguageChange}
            >
              {translate(HeaderEnum['locales.spanish'])} (Español)
            </DefaultButton>
          </li>
          <li>
            <DefaultButton
              className="tw-flex tw-w-full tw-items-center tw-justify-between tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[20.16px] tw-text-textBlack disabled:tw-animate-pulse disabled:tw-text-gray-600 max-nine:!tw-text-sm nine:tw-min-w-[unset]"
              onClick={() => handleChangeLanguage(Locales.IT)}
              disabled={doingLanguageChange}
            >
              {translate(HeaderEnum['locales.italian'])} (Italiano)
            </DefaultButton>
          </li>
          <li>
            <DefaultButton
              className="tw-flex tw-w-full tw-items-center tw-justify-between tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[20.16px] tw-text-textBlack disabled:tw-animate-pulse disabled:tw-text-gray-600 max-nine:!tw-text-sm nine:tw-min-w-[unset]"
              onClick={() => handleChangeLanguage(Locales.FR)}
              disabled={doingLanguageChange}
            >
              {translate(HeaderEnum['locales.french'])} (Français)
            </DefaultButton>
          </li>
          <li>
            <DefaultButton
              className="tw-flex tw-w-full tw-items-center tw-justify-between tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[20.16px] tw-text-textBlack disabled:tw-animate-pulse disabled:tw-text-gray-600 max-nine:!tw-text-sm nine:tw-min-w-[unset]"
              onClick={() => handleChangeLanguage(Locales.DE)}
              disabled={doingLanguageChange}
            >
              {translate(HeaderEnum['locales.german'])} (Deutsch)
            </DefaultButton>
          </li>
        </ul>
      </Transition>
      {/* LANGUAGE MENU */}
      <Transition
        show={currentSubMenuActive === 'settings'}
        enter="tw-transition-transform tw-duration-150"
        enterFrom="tw-transform tw--translate-x-full"
        enterTo="tw-transform tw-translate-x-0"
        leave="tw-transition-transform tw-duration-150"
        leaveFrom="tw-transform tw-translate-x-0"
        leaveTo="tw-transform tw--translate-x-full"
        as={'div'}
      >
        <ul className="tw-flex tw-list-none tw-flex-col tw-gap-4 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-platinum-gray tw-p-0 tw-pb-4 tw-pt-5">
          <li>
            <DefaultButton
              className="tw-flex tw-w-full tw-items-center tw-gap-4 tw-pb-2 tw-pl-1 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[20.16px] tw-text-charcoal-blue tw-shadow-sm max-nine:!tw-text-sm nine:tw-min-w-[unset]"
              onClick={() => setCurrentSubMenuActive('none')}
            >
              <Svgchevronleft width={6} height={12} className="!tw-text-charcoal-blue" />
              {translate(HeaderEnum['dashboard_menu.main.settings'])}
            </DefaultButton>
          </li>
          {uiItems.header.mobileMenu.dashboardMenu.dashboardMenuSettings.map(item => (
            <li className="tw-pb-2" key={item.tKey}>
              <a
                className="tw-flex tw-items-center tw-gap-2 !tw-fill-charcoal-blue !tw-text-charcoal-blue tw-no-underline"
                href={item.action}
              >
                <Image src={item.extraIcon} alt={item.tKey} width={24} height={24} />
                <p className="tw-mb-0 tw-pb-0 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[17.64px] tw-text-charcoal-blue">
                  {translate(HeaderEnum[item.tKey as keyof typeof HeaderEnum])}
                </p>
              </a>
            </li>
          ))}
        </ul>
      </Transition>
    </>
  );
}
